import React, { useState } from 'react';
import { Card, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const { Text } = Typography;

const AREA_OPTIONS = [
  { value: 'payroll', label: 'Payroll' },
  { value: 'leave', label: 'Leave' },
  { value: 'ot', label: 'Overtime / TOIL' },
  { value: 'eosb', label: 'EOSB' },
  { value: 'accrual', label: 'Accruals' },
  { value: 'employee', label: 'Employee lifecycle' },
  { value: 'setup', label: 'Setup' },
  { value: 'ess', label: 'ESS' },
];

const AuditLogPage: React.FC = () => {
  const [area, setArea] = useState<string | undefined>();

  const params = area ? { area } : undefined;
  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.auditLog(params),
    queryFn: () => hrmsService.listAuditLog(params),
  });

  const columns: ColumnsType<any> = [
    {
      title: 'When',
      dataIndex: 'created_at',
      width: 160,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY HH:mm') : '—'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Area',
      dataIndex: 'area',
      width: 120,
      render: (a: string) => <Tag>{a || '—'}</Tag>,
    },
    { title: 'Action', dataIndex: 'action', width: 160, render: (v: string) => v || '—' },
    {
      title: 'Entity',
      key: 'entity',
      width: 180,
      render: (_, r) => r.entity_type || '—',
    },
    {
      title: 'User',
      dataIndex: 'user_id',
      width: 120,
      ellipsis: true,
      render: (v: string, r) => r.user_name || r.actor_name || '—',
    },
    {
      title: 'Details',
      dataIndex: 'message',
      ellipsis: true,
      render: (v: string, r) => v || r.notes || r.detail || '—',
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="HRMS audit log"
          subtitle="Company HR actions — filter by functional area"
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Area"
            style={{ width: 200 }}
            value={area}
            onChange={setArea}
            options={AREA_OPTIONS}
          />
          <Text type="secondary">{rows.length} event(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey={(r) => r.id || `${r.created_at}-${r.action}-${r.entity_id}`}
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 900 }}
        pagination={{ pageSize: 25 }}
      />
    </WorkspaceLayout>
  );
};

export default AuditLogPage;
